import { PlanRestriction } from '../constraints/plan-restriction';
import { Color } from '../materials/color';
import { Material } from '../materials/material';
import { MaterialThickness } from '../materials/material-thickness';
import { Shape } from '../shape';
import { v4 as uuidv4 } from 'uuid';
import { Point } from '../utils/point';
import { WorkbenchFabricShapeRotationStateEnum } from 'src/app/enum/workbench/fabric-preview/workbench-fabric-shape-rotation-state-enum';
import { ConfigurationJamb } from './configuration-jamb';
import { ConfigurationCutaway } from './configuration-cutaway';
import { ConfigurationEdge } from './configuration-edge';
import { ConfigurationSplashback } from './configuration-splashback';
import { ConfigurationCutting } from './configuration-cutting';
import { ConfigurationSideCut } from './configuration-side-cut';
import { ShapeTypeEnum } from 'src/app/enum/workbench/shape-type.enum';
import {
  MAX_SUM_LENGTH_WIDTH,
  MIN_CUT_EDGE_LENGTH,
} from 'src/app/services/helpers/workbench-helpers/workbench-const-helper';

export class ConfigurationPlan {
  // Info basique
  id: string;
  name: string;
  length: number;
  width: number;
  diameter: number;
  displayOrder: number;
  shapeRotationState: WorkbenchFabricShapeRotationStateEnum;
  isInGroup: boolean;
  isFlip: boolean;
  // Configuration plan
  shapeId: number;
  materialId: number;
  materialThicknessId: number;
  colorId: number;
  groupReferenceId: string;

  // Jointure
  shape: Shape;
  material: Material;
  materialThickness: MaterialThickness;
  color: Color;
  origin: Point;

  // Finitions
  configurationEdges: ConfigurationEdge[];
  configurationJambs: ConfigurationJamb[];
  configurationCutaways: ConfigurationCutaway[];
  configurationCuttings: ConfigurationCutting[];
  configurationSideCuts: ConfigurationSideCut[];

  // Crédence
  configurationSplashbacks: ConfigurationSplashback[];

  planRestrictions: PlanRestriction;

  constructor(displayOrder: number) {
    this.id = uuidv4();
    this.displayOrder = displayOrder;
    this.name = 'Plan n° ' + displayOrder;
    this.shapeRotationState = WorkbenchFabricShapeRotationStateEnum.Droite;
    this.isInGroup = false;
    this.configurationJambs = [];
    this.configurationCutaways = [];
    this.configurationSplashbacks = [];
  }
}

export function isPlanDimensionsValid(plan: ConfigurationPlan) {
  let dimensionsValid = true;

  if (plan.shape?.needDimensions) {
    dimensionsValid =
      dimensionsValid &&
      plan.length != null &&
      plan.length > 0 &&
      plan.width != null &&
      plan.width > 0 &&
      plan.shape?.constraintGroups.some(cg => {
        return (
          cg.minLength <= plan.length &&
          cg.maxLength >= plan.length &&
          cg.minWidth <= plan.width &&
          cg.maxWidth >= plan.width
        );
      }) &&
      (plan.shape.shapeType !== ShapeTypeEnum.Angle || areAngleDimensionsValid(plan));
  }

  if (plan.shape?.needDiameter) {
    dimensionsValid =
      dimensionsValid &&
      plan.diameter != null &&
      plan.diameter > 0 &&
      plan.shape?.constraintGroups.some(cg => {
        return cg.minDiameter <= plan.diameter && cg.maxDiameter >= plan.diameter;
      });
  }

  return plan.displayOrder != null && plan.shapeId != null && plan.shape != null && dimensionsValid;
}

export function isPlanColorValid(plan: ConfigurationPlan) {
  return (
    plan.materialId != null &&
    plan.material != null &&
    plan.materialThicknessId != null &&
    plan.materialThickness != null &&
    plan.colorId != null &&
    plan.color != null &&
    !plan.color.disabled
  );
}

/**
 * Check que le plan d'angle corresponde aux contraintes suivantes :
 * - la longueur doit être plus grand que la largeur.
 * - longueur + largeur doit être inférieur à 1600mm
 * - la longueur du côté coupé doit faire au minimum 80mm
 * @param plan
 */
export function areAngleDimensionsValid(plan: ConfigurationPlan): boolean {
  const lengthSmallSquare = plan.length - plan.width; //longueur du petit carré dont la diagonale est la longueur du pan coupé

  return (
    plan.length > plan.width && //longueur doit être plus grande que largeur
    plan.length + plan.width <= MAX_SUM_LENGTH_WIDTH && // il faut respecter longueur + largeur <= 1600
    Math.sqrt(lengthSmallSquare * lengthSmallSquare * 2) >= MIN_CUT_EDGE_LENGTH
  );
}
